import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import type { InstituteListParams } from '../types/institute.types';
import { toInstituteFilterSearch, toInstituteFilterValues } from '../utils/instituteFilters';

const route = getRouteApi('/_auth/settings/institutes/');

const normalizeFilterValues = (values: Record<string, string>) =>
  toInstituteFilterValues(toInstituteFilterSearch(values));

export function useInstituteListFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toInstituteFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toInstituteFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const nameSearch = useUrlSyncedSearch({
    appliedText: search.name,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, name: text }),
        replace: true,
      });
    },
  });

  const queryParams = useMemo<InstituteListParams | null>(() => {
    const { instituteTypeId, examBoardId, districtId, thanaId, status, eiinNumber, name } = search;
    if (instituteTypeId === undefined || districtId === undefined) return null;
    return {
      instituteTypeId,
      examBoardId,
      districtId,
      thanaId,
      status,
      eiinNumber,
      name,
    };
  }, [search]);

  const clearPendingId = useCallback(() => {
    void navigate({ search: (previous) => ({ ...previous, _pendingId: undefined }) });
  }, [navigate]);

  return {
    ...filters,
    ...nameSearch,
    appliedSearchText: search.name,
    queryParams,
    pendingId: search._pendingId,
    clearPendingId,
  };
}
